import { Box, FileText, Headphones, Image, Video } from 'lucide-react'; // Import icons, replaced Cube with Box
import { useNavigate } from 'react-router';
import { Card } from '../../../components/atoms/Card';
import { Exhibit } from '../../../types/models'; // Import Exhibit type
import { cn } from '../../../utils/cn';

interface ExhibitCardProps {
    exhibit: Exhibit; // Accept the full Exhibit object
    className?: string;
}

export const ExhibitCard = ({ exhibit, className }: ExhibitCardProps) => {
    const navigate = useNavigate();

    // Get default language translation or first available
    const translation = exhibit.translations.find((t) => t.language_code === exhibit.default_language) || exhibit.translations[0];

    const handleClick = () => {
        navigate(`/exhibits/${exhibit.id}`);
    };

    // Truncate description
    const shortDescription = translation?.description
        ? translation.description.length > 100
            ? translation.description.substring(0, 100) + '...'
            : translation.description
        : '';

    return (
        <Card
            as="div"
            className={cn('flex h-full w-full flex-shrink-0 cursor-pointer flex-col overflow-hidden', className)} // Added h-full and flex-col
            variant="elevated"
            padding="none"
            hoverable
            onClick={handleClick}
        >
            {/* Exhibit image */}
            <div className="relative aspect-video w-full overflow-hidden bg-gray-100 dark:bg-gray-800">
                {exhibit.thumbnail_url ? (
                    <img
                        src={'/storage/' + exhibit.thumbnail_url}
                        alt={translation?.title || 'Exhibit image'}
                        className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                        loading="lazy"
                    />
                ) : (
                    <div className="flex h-full w-full items-center justify-center text-gray-400 dark:text-gray-600">
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            width="40"
                            height="40"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="1"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                        >
                            <path d="M4 22h16a2 2 0 0 0 2-2V7.5L14.5 2H6a2 2 0 0 0-2 2v4"></path>
                            <polyline points="14 2 14 8 20 8"></polyline>
                            <path d="M3 15h6"></path>
                            <path d="M6 18V12"></path>
                        </svg>
                    </div>
                )}

                {/* Media Type Icons */}
                <div className="absolute top-2 right-2 flex space-x-1">
                    {/* 3D Model Icon */}
                    {exhibit.model_url && (
                        <span className="rounded bg-black/50 p-0.5 text-white" title="3D Model">
                            <Box className="h-4 w-4" /> {/* Replaced Cube with Box */}
                        </span>
                    )}
                    {/* Video Icon */}
                    {exhibit.video_url && (
                        <span className="rounded bg-black/50 p-0.5 text-white" title="Video">
                            <Video className="h-4 w-4" />
                        </span>
                    )}
                    {/* Audio Guide Icon */}
                    {exhibit.audio_guide_url && (
                        <span className="rounded bg-black/50 p-0.5 text-white" title="Audio Guide">
                            <Headphones className="h-4 w-4" />
                        </span>
                    )}
                    {/* Gallery/Photos Icon */}
                    {exhibit.gallery_urls && exhibit.gallery_urls.length > 0 && (
                        <span className="rounded bg-black/50 p-0.5 text-white" title="Gallery">
                            <Image className="h-4 w-4" />
                        </span>
                    )}
                    {/* Documents Icon */}
                    {exhibit.documents_urls && exhibit.documents_urls.length > 0 && (
                        <span className="rounded bg-black/50 p-0.5 text-white" title="Documents">
                            <FileText className="h-4 w-4" />
                        </span>
                    )}
                </div>
            </div>

            {/* Exhibit info */}
            <div className="flex flex-1 flex-col p-4">
                {' '}
                {/* Added flex-1 and flex-col */}
                <h3 className="line-clamp-1 font-medium text-gray-900 dark:text-white">{translation?.title || 'Untitled Exhibit'}</h3>
                {(exhibit.artist || exhibit.year_created) && (
                    <div className="mt-1 text-sm text-gray-600 dark:text-gray-400">
                        {exhibit.artist && <span>{exhibit.artist}</span>}
                        {exhibit.artist && exhibit.year_created && <span> • </span>}
                        {exhibit.year_created && <span>{exhibit.year_created}</span>}
                    </div>
                )}
                {shortDescription && <p className="mt-1 line-clamp-2 text-sm text-gray-600 dark:text-gray-300">{shortDescription}</p>}
                {/* View more link */}
                <div className="text-primary mt-auto flex items-center pt-2 text-sm">
                    {' '}
                    {/* Added mt-auto and pt-2 */}
                    View exhibit
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="16"
                        height="16"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="ml-1"
                    >
                        <polyline points="9 18 15 12 9 6"></polyline>
                    </svg>
                </div>
            </div>
        </Card>
    );
};
